import { useEffect, useMemo, useState } from "react"
import { MdAccountTree, MdAdsClick, MdWarningAmber } from "react-icons/md"
import Modal from "../../components/Modal"
import Button from "../../components/Form/Button"
import Checkbox from "../../components/Form/Checkbox"
import Input from "../../components/Form/Input"
import Loading from "../layouts/Loading"
import api from "../../services/api"
import styles from "./googleIntegration.module.css"

const PAGE_SIZE = 100

const ModalGoogleAdAccountSelection = ({ connection, closeModal, onSaved }) => {
    const [accounts, setAccounts] = useState([])
    const [selected, setSelected] = useState(new Set())
    const [search, setSearch] = useState("")
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        api.post("/integrations/google/adAccounts/discover", { google_user_account_id: connection._id })
            .then(response => {
                const data = response?.data?.data || {}
                setAccounts(data.accounts || [])
                setSelected(new Set((data.selected_customer_ids || []).map(String)))
            })
            .catch(err => setError(err?.response?.data?.message || "Não foi possível consultar as contas acessíveis."))
            .finally(() => setLoading(false))
    }, [connection._id])

    const selectable = useMemo(() => accounts.filter(account => !account.manager), [accounts])
    const filtered = useMemo(() => {
        const term = search.trim().toLocaleLowerCase("pt-BR")
        return accounts.filter(account => !term || `${account.descriptive_name || ""} ${account.customer_id}`.toLocaleLowerCase("pt-BR").includes(term))
    }, [accounts, search])
    const visible = filtered.slice(0, visibleCount)

    const toggle = customerId => setSelected(previous => {
        const next = new Set(previous)
        if (next.has(customerId)) next.delete(customerId)
        else next.add(customerId)
        return next
    })

    const selectFiltered = () => setSelected(previous => {
        const next = new Set(previous)
        filtered.filter(account => !account.manager).forEach(account => next.add(String(account.customer_id)))
        return next
    })
    const clearFiltered = () => setSelected(previous => {
        const next = new Set(previous)
        filtered.forEach(account => next.delete(String(account.customer_id)))
        return next
    })

    const save = async () => {
        setSaving(true)
        setError("")
        try {
            await api.patch("/integrations/google/adAccounts/selection", { google_user_account_id: connection._id, customer_ids: [...selected] })
            await onSaved?.()
            closeModal()
        } catch (err) {
            setError(err?.response?.data?.message || "Não foi possível salvar as contas autorizadas.")
        } finally { setSaving(false) }
    }

    return <Modal icon={<MdAdsClick />} title="Contas autorizadas" description={`Escolha quais contas de ${connection.email || "esta conexão"} poderão ser vistas e utilizadas nesta organização.`} closeModal={saving ? () => {} : closeModal} closeOnEsc={!saving} bodyClassName={styles.accountSelectionBody}>
        {loading ? <div className={styles.selectionLoading}><Loading /><p>Consultando as contas diretamente no Google Ads…</p></div> : <>
            <div className={styles.selectionNotice}><MdWarningAmber /><span>Contas não selecionadas não serão salvas, exibidas em dashboards nem aceitas em conversões. Contas MCC são mostradas apenas como referência e não podem ser usadas como anunciante.</span></div>
            <div className={styles.selectionToolbar}>
                <Input type="search" value={search} onChange={event => { setSearch(event.target.value); setVisibleCount(PAGE_SIZE) }} placeholder="Pesquisar por nome ou ID" aria-label="Pesquisar contas Google Ads" />
                <span><strong>{selected.size}</strong> de {selectable.length} selecionadas</span>
            </div>
            <div className={styles.selectionQuickActions}><Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={selectFiltered}>Selecionar resultados</Button><Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={clearFiltered}>Limpar resultados</Button></div>
            {error && <div className={styles.selectionError}>{error}</div>}
            <div className={styles.selectionList}>
                {visible.map(account => {
                    const id = String(account.customer_id)
                    return <button type="button" key={`${id}-${account.login_customer_id || "direct"}`} className={account.manager ? styles.managerSelectionRow : ""} disabled={account.manager} aria-pressed={selected.has(id)} onClick={() => toggle(id)}>
                        <Checkbox checked={selected.has(id)} />
                        <span className={styles.selectionAccountIcon}>{account.manager ? <MdAccountTree /> : <MdAdsClick />}</span>
                        <span><strong>{account.descriptive_name || "Conta Google Ads"}</strong><small>ID {id}{account.currency_code ? ` · ${account.currency_code}` : ""}</small></span>
                        <em>{account.manager ? "MCC" : account.login_customer_id ? `via MCC ${account.login_customer_id}` : "Acesso direto"}</em>
                    </button>
                })}
                {!visible.length && <p className={styles.noSelectionResults}>Nenhuma conta encontrada.</p>}
                {visibleCount < filtered.length && <Button type="button" variant="secondary" fullWidth={false} onClick={() => setVisibleCount(value => value + PAGE_SIZE)}>Carregar mais {Math.min(PAGE_SIZE, filtered.length - visibleCount)}</Button>}
            </div>
            <div className={styles.selectionFooter}><Button type="button" variant="secondary" fullWidth={false} onClick={closeModal}>Cancelar</Button><Button type="button" fullWidth={false} onClick={save}>Salvar {selected.size} conta(s)</Button></div>
        </>}
        {saving && <Loading fullPage={true} />}
    </Modal>
}

export default ModalGoogleAdAccountSelection
